/**
 * EventScope
 *
 * Root-bound event registration with cleanup (AbortController) and idempotent binding.
 *
 * Motivation:
 * - Prevent double-firing caused by duplicate addEventListener
 * - Prefer binding to a specific root (page container / modal body) instead of document
 * - Enable cleanup on modal close / DOM removal
 */

import { FormBase } from "@/util/form-base-form";
import type { EventRoot } from "./event-scope-types";


class EventScopeFormBase extends FormBase {
    public getClosest(target: EventTarget | Element | null | undefined, selector: string): HTMLElement | null {
        return this.closest(target, selector, HTMLElement);
    }
}

const eventScopeFormBase = new EventScopeFormBase();

function createOnceKeysSet(): Set<string> {
    return new Set();
}

function createScopesByRootMap(): WeakMap<EventRoot, Map<string, EventScope>> {
    return new WeakMap();
}

class EventScope {
    public root: EventRoot;

    private _controller: AbortController;

    private _onceKeys: Set<string>;

    public constructor(root: EventRoot) {
        this.root = root;
        this._controller = new AbortController();
        this._onceKeys = createOnceKeysSet();
    }

    public once(key: string, fn: () => void): void {
        if (this._onceKeys.has(key)) return;
        this._onceKeys.add(key);
        fn();
    }

    public on(type: string, listener: EventListenerOrEventListenerObject, options: AddEventListenerOptions = {}): void {
        const merged = { ...options, signal: this._controller.signal };
        this.root.addEventListener(type, listener, merged);
    }

    public delegate(
        type: string,
        selector: string,
        handler: (ev: Event, matched: Element) => void,
        options: AddEventListenerOptions = {},
    ): void {
        this.on(
            type,
            (ev: Event) => {
                const target = ev.target;
                if (!(target instanceof Element)) return;

                const matched = eventScopeFormBase.getClosest(target, selector);
                if (!matched) return;

                if (!isWithinRoot(this.root, matched)) return;

                handler(ev, matched);
            },
            options,
        );
    }

    public dispose(): void {
        this._controller.abort();
    }
}

const scopesByRoot = createScopesByRootMap();

export function getEventScope(root: EventRoot, key: string): EventScope {
    const map = scopesByRoot.get(root) ?? new Map<string, EventScope>();
    if (!scopesByRoot.has(root)) {
        scopesByRoot.set(root, map);
    }

    const existing = map.get(key);
    if (existing) return existing;

    const scope = new EventScope(root);
    map.set(key, scope);
    return scope;
}

export function disposeEventScope(root: EventRoot, key: string): void {
    const map = scopesByRoot.get(root);
    if (!map) return;

    const scope = map.get(key);
    if (!scope) return;

    scope.dispose();
    map.delete(key);
}

function isWithinRoot(root: EventRoot, el: Element): boolean {
    if (root instanceof Document) {
        return root.documentElement ? root.documentElement.contains(el) : true;
    }
    if (root instanceof Window) {
        return root.document?.documentElement ? root.document.documentElement.contains(el) : true;
    }
    return root.contains(el);
}

export { EventScope };
